<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { normalizeDashboardLocale } from 'dashboard/i18n/dashboardLocale';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountFooter from './components/AccountFooter.vue';
import InactivityResolution from './components/InactivityResolution.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import AssignmentWelcome from './components/AssignmentWelcome.vue';
import AgentCapacitySettings from './components/AgentCapacitySettings.vue';
import ResolveSettings from './components/ResolveSettings.vue';
import SectionLayout from './components/SectionLayout.vue';
import TemporaryPrivateMessages from './components/TemporaryPrivateMessages.vue';
import PixKeys from './components/PixKeys.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';

export default {
  components: {
    BaseSettingsHeader,
    NextButton,
    DateField,
    AccountFooter,
    InactivityResolution,
    AccountDelete,
    AudioTranscription,
    AssignmentWelcome,
    AgentCapacitySettings,
    ResolveSettings,
    SectionLayout,
    TemporaryPrivateMessages,
    PixKeys,
    WithLabel,
    NextInput,
  },
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'pt_BR',
      domain: '',
      supportEmail: '',
      companyProfile: {
        cnpj: '',
        razao_social: '',
        nome_fantasia: '',
        data_abertura: '',
        email: '',
        telefone: '',
        endereco: '',
        cidade: '',
        estado: '',
        cep: '',
        site: '',
      },
      isFetchingCompanyProfile: false,
      agentConversationAccessMode: 'reply_lock',
      agentOnlyAssignedConversations: false,
      unifyContactConversationsAcrossInboxes: false,
      features: {},
      activeSettingsSection: 'company-settings',
      settingsScrollFrame: null,
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnTalkviaCloud: 'globalConfig/isOnTalkviaCloud',
    }),
    settingsNavigation() {
      return [
        {
          id: 'company-settings',
          label: this.$t('GENERAL_SETTINGS.NAVIGATION.COMPANY'),
          icon: 'i-lucide-building-2',
        },
        {
          id: 'pix-key-settings',
          label: this.$t('PIX_KEYS.TITLE'),
          icon: 'i-simple-icons-pix',
        },
        {
          id: 'service-settings',
          label: this.$t('GENERAL_SETTINGS.NAVIGATION.SERVICE'),
          icon: 'i-lucide-messages-square',
        },
        {
          id: 'capacity-settings',
          label: this.$t('GENERAL_SETTINGS.NAVIGATION.CAPACITY'),
          icon: 'i-lucide-gauge',
        },
        ...(this.isOnTalkviaCloud
          ? [
              {
                id: 'advanced-settings',
                label: this.$t('GENERAL_SETTINGS.NAVIGATION.ADVANCED'),
                icon: 'i-lucide-sliders-horizontal',
              },
            ]
          : []),
      ];
    },
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    companyOpeningAge() {
      if (!this.companyProfile.data_abertura) return '-';

      const openingDate = new Date(
        `${this.companyProfile.data_abertura}T00:00:00`
      );
      if (Number.isNaN(openingDate.getTime())) return '-';

      const now = new Date();
      let years = now.getFullYear() - openingDate.getFullYear();
      let months = now.getMonth() - openingDate.getMonth();
      if (now.getDate() < openingDate.getDate()) months -= 1;
      if (months < 0) {
        years -= 1;
        months += 12;
      }

      return `${years} ano(s) e ${months} mês(es)`;
    },
    agentConversationAccessModeOptions() {
      return [
        {
          value: 'shared',
          icon: 'i-lucide-users',
          label: this.$t(
            'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.OPTIONS.SHARED'
          ),
          description: this.$t(
            'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.OPTIONS_DESCRIPTION.SHARED'
          ),
        },
        {
          value: 'reply_lock',
          icon: 'i-lucide-user-check',
          label: this.$t(
            'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.OPTIONS.REPLY_LOCK'
          ),
          description: this.$t(
            'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.OPTIONS_DESCRIPTION.REPLY_LOCK'
          ),
        },
        {
          value: 'private_assignee',
          icon: 'i-lucide-lock-keyhole',
          label: this.$t(
            'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.OPTIONS.PRIVATE_ASSIGNEE'
          ),
          description: this.$t(
            'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.OPTIONS_DESCRIPTION.PRIVATE_ASSIGNEE'
          ),
        },
      ];
    },
  },
  watch: {
    'currentAccount.id'(id) {
      if (id) {
        this.initializeAccount();
      }
    },
  },
  mounted() {
    // Account already in the store (navigated in): seed immediately.
    if (this.currentAccount.id) {
      this.initializeAccount();
    }
    document.addEventListener('scroll', this.handleSettingsScroll, true);
    window.addEventListener('resize', this.handleSettingsScroll);
    this.$nextTick(this.updateActiveSettingsSection);
  },
  beforeUnmount() {
    document.removeEventListener('scroll', this.handleSettingsScroll, true);
    window.removeEventListener('resize', this.handleSettingsScroll);
    if (this.settingsScrollFrame) {
      window.cancelAnimationFrame(this.settingsScrollFrame);
    }
  },
  methods: {
    scrollToSettingsSection(sectionId) {
      this.activeSettingsSection = sectionId;
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    handleSettingsScroll() {
      if (this.settingsScrollFrame) return;

      this.settingsScrollFrame = window.requestAnimationFrame(() => {
        this.settingsScrollFrame = null;
        this.updateActiveSettingsSection();
      });
    },
    updateActiveSettingsSection() {
      const sections = this.settingsNavigation
        .map(item => document.getElementById(item.id))
        .filter(Boolean);
      if (!sections.length) return;

      const activationLine = Math.min(window.innerHeight * 0.3, 240);
      const activeSection =
        [...sections]
          .reverse()
          .find(
            section => section.getBoundingClientRect().top <= activationLine
          ) || sections[0];
      this.activeSettingsSection = activeSection.id;
    },
    async initializeAccount() {
      try {
        const { name, locale, id, domain, support_email, features } =
          this.getAccount(this.accountId);

        const effectiveLocale = this.uiSettings?.locale || locale;
        if (effectiveLocale) {
          this.$root.$i18n.locale = normalizeDashboardLocale(effectiveLocale);
        }
        this.name = name;
        this.locale = normalizeDashboardLocale(locale);
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.companyProfile = {
          ...this.companyProfile,
          ...(this.currentAccount?.custom_attributes?.company_profile || {}),
        };
        this.agentConversationAccessMode =
          this.currentAccount?.settings?.agent_conversation_access_mode ||
          'reply_lock';
        this.agentOnlyAssignedConversations =
          this.currentAccount?.settings?.agent_only_assigned_conversations ===
          true;
        this.unifyContactConversationsAcrossInboxes =
          this.currentAccount?.settings
            ?.unify_contact_conversations_across_inboxes === true;
        this.features = features;
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          company_profile: this.companyProfile,
          agent_conversation_access_mode: this.agentConversationAccessMode,
          agent_only_assigned_conversations:
            this.agentOnlyAssignedConversations,
          unify_contact_conversations_across_inboxes:
            this.unifyContactConversationsAcrossInboxes,
        });
        // If user locale is set, update the locale with user locale
        const updatedLocale = this.uiSettings?.locale || this.locale;
        if (updatedLocale) {
          this.$root.$i18n.locale = normalizeDashboardLocale(updatedLocale);
        }
        this.getAccount(this.id).locale = this.locale;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },
    cleanCnpj(value) {
      return String(value || '').replace(/\D/g, '');
    },
    formatAddressFromBrasilApi(data) {
      return [data.logradouro, data.numero, data.complemento, data.bairro]
        .filter(Boolean)
        .join(', ');
    },
    async lookupCompanyByCnpj() {
      const cnpj = this.cleanCnpj(this.companyProfile.cnpj);
      if (cnpj.length !== 14) return;

      this.isFetchingCompanyProfile = true;
      try {
        const response = await fetch(
          `https://brasilapi.com.br/api/cnpj/v1/${cnpj}`
        );
        if (!response.ok) throw new Error('CNPJ não encontrado');
        const data = await response.json();
        this.companyProfile = {
          ...this.companyProfile,
          cnpj,
          razao_social: data.razao_social || '',
          nome_fantasia: data.nome_fantasia || '',
          data_abertura: data.data_inicio_atividade || '',
          email: data.email || '',
          telefone: data.ddd_telefone_1 || data.ddd_telefone_2 || '',
          endereco: this.formatAddressFromBrasilApi(data),
          cidade: data.municipio || '',
          estado: data.uf || '',
          cep: data.cep || '',
        };
      } catch (error) {
        useAlert('Não foi possível localizar o CNPJ na BrasilAPI.');
      } finally {
        this.isFetchingCompanyProfile = false;
      }
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="settings-layout w-full max-w-6xl !overflow-visible ltr:mr-auto rtl:ml-auto"
  >
    <BaseSettingsHeader
      :title="$t('GENERAL_SETTINGS.TITLE')"
      class="settings-header"
    />
    <nav
      class="settings-navigation sticky top-0 z-20 py-2 lg:top-4 lg:py-0"
      :aria-label="$t('GENERAL_SETTINGS.TITLE')"
    >
      <div class="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
        <button
          v-for="item in settingsNavigation"
          :key="item.id"
          type="button"
          class="group flex min-w-max items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors lg:w-full lg:py-3"
          :class="
            activeSettingsSection === item.id
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          :aria-current="
            activeSettingsSection === item.id ? 'location' : undefined
          "
          @click="scrollToSettingsSection(item.id)"
        >
          <span
            class="size-4 shrink-0"
            :class="[
              item.icon,
              activeSettingsSection === item.id
                ? 'text-n-brand'
                : 'text-n-slate-10 group-hover:text-n-slate-11',
            ]"
          />
          <span>{{ item.label }}</span>
        </button>
      </div>
    </nav>
    <div class="settings-content min-w-0 pb-8">
      <main class="min-w-0">
        <div id="company-settings" class="scroll-mt-20">
          <SectionLayout
            :title="$t('GENERAL_SETTINGS.SECTIONS.COMPANY.TITLE')"
            :description="$t('GENERAL_SETTINGS.SECTIONS.COMPANY.NOTE')"
            icon="i-lucide-building-2"
            class="!pt-0"
          >
            <form
              v-if="!uiFlags.isFetchingItem"
              class="grid gap-4"
              @submit.prevent="updateAccount"
            >
              <WithLabel
                name="account-name"
                :has-error="v$.name.$error"
                :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
                :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
              >
                <NextInput
                  v-model="name"
                  type="text"
                  class="w-full"
                  :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
                  @blur="v$.name.$touch"
                />
              </WithLabel>
              <WithLabel
                name="site-language"
                :has-error="v$.locale.$error"
                :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
                :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
              >
                <select v-model="locale" class="!mb-0 text-sm">
                  <option
                    v-for="lang in languagesSortedByCode"
                    :key="lang.iso_639_1_code"
                    :value="lang.iso_639_1_code"
                  >
                    {{ lang.name }}
                  </option>
                </select>
              </WithLabel>
              <p class="mb-0 mt-2 text-sm font-medium text-n-slate-12">
                {{ $t('GENERAL_SETTINGS.SECTIONS.COMPANY.PROFILE') }}
              </p>
              <div
                class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
              >
                <div class="grid gap-4 md:grid-cols-2">
                  <WithLabel name="company-cnpj" label="CNPJ">
                    <NextInput
                      v-model="companyProfile.cnpj"
                      type="text"
                      class="w-full"
                      placeholder="00.000.000/0000-00"
                      @blur="lookupCompanyByCnpj"
                    />
                    <template #help>
                      {{
                        isFetchingCompanyProfile
                          ? 'Consultando BrasilAPI...'
                          : 'Ao sair do campo, os dados serão consultados automaticamente.'
                      }}
                    </template>
                  </WithLabel>
                  <WithLabel
                    name="company-opening-date"
                    label="Data da abertura"
                  >
                    <DateField
                      v-model="companyProfile.data_abertura"
                      class="w-full"
                    />
                    <template #help>
                      Tempo de abertura: {{ companyOpeningAge }}
                    </template>
                  </WithLabel>
                  <WithLabel name="company-legal-name" label="Razão social">
                    <NextInput
                      v-model="companyProfile.razao_social"
                      type="text"
                      class="w-full"
                      placeholder="Razão social"
                    />
                  </WithLabel>
                  <WithLabel name="company-trade-name" label="Nome fantasia">
                    <NextInput
                      v-model="companyProfile.nome_fantasia"
                      type="text"
                      class="w-full"
                      placeholder="Nome fantasia"
                    />
                  </WithLabel>
                  <WithLabel name="company-email" label="E-mail">
                    <NextInput
                      v-model="companyProfile.email"
                      type="email"
                      class="w-full"
                      placeholder="empresa@email.com"
                    />
                  </WithLabel>
                  <WithLabel name="company-phone" label="Telefone">
                    <NextInput
                      v-model="companyProfile.telefone"
                      type="text"
                      class="w-full"
                      placeholder="(00) 00000-0000"
                    />
                  </WithLabel>
                  <WithLabel name="company-site" label="Site da empresa">
                    <NextInput
                      v-model="companyProfile.site"
                      type="url"
                      class="w-full"
                      placeholder="https://www.suaempresa.com.br"
                    />
                    <template #help>
                      Usado para liberar domínios próprios nas captações de
                      leads.
                    </template>
                  </WithLabel>
                </div>
                <WithLabel name="company-address" label="Endereço">
                  <NextInput
                    v-model="companyProfile.endereco"
                    type="text"
                    class="w-full"
                    placeholder="Rua, número, complemento, bairro"
                  />
                </WithLabel>
                <div class="grid gap-4 md:grid-cols-3">
                  <WithLabel name="company-city" label="Cidade">
                    <NextInput
                      v-model="companyProfile.cidade"
                      type="text"
                      class="w-full"
                    />
                  </WithLabel>
                  <WithLabel name="company-state" label="Estado">
                    <NextInput
                      v-model="companyProfile.estado"
                      type="text"
                      class="w-full"
                    />
                  </WithLabel>
                  <WithLabel name="company-zip" label="CEP">
                    <NextInput
                      v-model="companyProfile.cep"
                      type="text"
                      class="w-full"
                    />
                  </WithLabel>
                </div>
              </div>
              <div>
                <NextButton blue :is-loading="isUpdating" type="submit">
                  {{ $t('GENERAL_SETTINGS.SECTIONS.COMPANY.SUBMIT') }}
                </NextButton>
              </div>
            </form>
            <woot-loading-state v-else />
          </SectionLayout>
        </div>
        <div id="pix-key-settings" class="scroll-mt-20">
          <PixKeys :key="accountId" :company-profile="companyProfile" />
        </div>
        <div id="service-settings" class="scroll-mt-20">
          <SectionLayout
            :title="$t('GENERAL_SETTINGS.SECTIONS.SERVICE.TITLE')"
            :description="$t('GENERAL_SETTINGS.SECTIONS.SERVICE.NOTE')"
            icon="i-lucide-messages-square"
            with-border
          >
            <form
              v-if="!uiFlags.isFetchingItem"
              class="grid gap-4"
              @submit.prevent="updateAccount"
            >
              <WithLabel
                name="agent-conversation-access-mode"
                :label="
                  $t(
                    'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.LABEL'
                  )
                "
              >
                <div
                  class="grid grid-cols-1 gap-3 xl:grid-cols-3"
                  role="radiogroup"
                >
                  <button
                    v-for="option in agentConversationAccessModeOptions"
                    :key="option.value"
                    type="button"
                    role="radio"
                    class="group flex min-h-44 w-full flex-col rounded-xl border p-5 text-left transition-all"
                    :aria-checked="agentConversationAccessMode === option.value"
                    :class="
                      agentConversationAccessMode === option.value
                        ? 'border-n-brand bg-n-alpha-2 shadow-[0_0_0_1px_var(--color-n-brand)]'
                        : 'border-n-weak bg-n-solid-2 hover:border-n-slate-8 hover:bg-n-alpha-1'
                    "
                    @click="agentConversationAccessMode = option.value"
                  >
                    <div class="flex w-full items-start justify-between gap-4">
                      <span
                        class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                        :class="{
                          'text-n-teal-11':
                            agentConversationAccessMode === option.value,
                        }"
                      >
                        <span class="size-5" :class="option.icon" />
                      </span>
                      <span
                        class="inline-flex size-5 shrink-0 items-center justify-center rounded-full border"
                        :class="
                          agentConversationAccessMode === option.value
                            ? 'border-n-brand bg-n-brand text-white'
                            : 'border-n-weak text-transparent group-hover:border-n-slate-8'
                        "
                      >
                        <span class="i-lucide-check size-3" />
                      </span>
                    </div>
                    <div class="mt-5">
                      <p class="text-base font-medium text-n-slate-12">
                        {{ option.label }}
                      </p>
                      <p class="mt-2 text-sm leading-6 text-n-slate-11">
                        {{ option.description }}
                      </p>
                    </div>
                  </button>
                </div>
                <template #help>
                  {{
                    $t(
                      'GENERAL_SETTINGS.FORM.AGENT_CONVERSATION_ACCESS_MODE.HELP'
                    )
                  }}
                </template>
              </WithLabel>
              <WithLabel
                name="agent-only-assigned-conversations"
                :label="
                  $t(
                    'GENERAL_SETTINGS.FORM.AGENT_ONLY_ASSIGNED_CONVERSATIONS.LABEL'
                  )
                "
              >
                <label
                  class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                >
                  <input
                    v-model="agentOnlyAssignedConversations"
                    type="checkbox"
                    class="mt-1"
                  />
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{
                        $t(
                          'GENERAL_SETTINGS.FORM.AGENT_ONLY_ASSIGNED_CONVERSATIONS.TITLE'
                        )
                      }}
                    </span>
                    <span class="mt-1 block text-sm leading-6 text-n-slate-11">
                      {{
                        $t(
                          'GENERAL_SETTINGS.FORM.AGENT_ONLY_ASSIGNED_CONVERSATIONS.DESCRIPTION'
                        )
                      }}
                    </span>
                  </span>
                </label>
              </WithLabel>
              <WithLabel
                name="unify-contact-conversations-across-inboxes"
                label="Unificar histórico de conversas do mesmo contato"
              >
                <label
                  class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                >
                  <input
                    v-model="unifyContactConversationsAcrossInboxes"
                    type="checkbox"
                    class="mt-1"
                  />
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      Reunir, no dashboard, as mensagens de todas as conversas
                      do mesmo contato na conta
                    </span>
                    <span class="mt-1 block text-sm leading-6 text-n-slate-11">
                      Quando estiver ligado, ao abrir uma conversa o histórico
                      também carrega mensagens das outras conversas desse
                      contato, separando os blocos pela caixa de entrada de
                      origem.
                    </span>
                  </span>
                </label>
              </WithLabel>
              <WithLabel
                v-if="featureCustomReplyDomainEnabled"
                name="custom-domain"
                :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
              >
                <NextInput
                  v-model="domain"
                  type="text"
                  class="w-full"
                  :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
                />
                <template #help>
                  {{
                    featureInboundEmailEnabled &&
                    $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
                  }}

                  {{
                    featureCustomReplyDomainEnabled &&
                    $t(
                      'GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED'
                    )
                  }}
                </template>
              </WithLabel>
              <WithLabel
                v-if="featureCustomReplyEmailEnabled"
                name="support-email"
                :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
              >
                <NextInput
                  v-model="supportEmail"
                  type="text"
                  class="w-full"
                  :placeholder="
                    $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
                  "
                />
              </WithLabel>
              <div>
                <NextButton blue :is-loading="isUpdating" type="submit">
                  {{ $t('GENERAL_SETTINGS.SECTIONS.SERVICE.SUBMIT') }}
                </NextButton>
              </div>
            </form>
          </SectionLayout>
          <InactivityResolution v-if="showAutoResolutionConfig" />
          <ResolveSettings />
          <AssignmentWelcome />
          <TemporaryPrivateMessages />
          <AudioTranscription />
        </div>
        <div id="capacity-settings" class="scroll-mt-20">
          <AgentCapacitySettings />
        </div>
        <div
          v-if="!uiFlags.isFetchingItem && isOnTalkviaCloud"
          id="advanced-settings"
          class="scroll-mt-20"
        >
          <AccountDelete />
        </div>
        <AccountFooter />
      </main>
    </div>
  </div>
</template>

<style scoped>
.settings-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem 0;
}

.settings-header,
.settings-navigation,
.settings-content {
  grid-column: 1;
}

@media (min-width: 1024px) {
  .settings-layout {
    grid-template-columns: 12.5rem minmax(0, 1fr);
    column-gap: 2.5rem;
  }

  .settings-header {
    grid-column: 1 / -1;
  }

  .settings-navigation {
    grid-column: 1;
    align-self: start;
  }

  .settings-content {
    grid-column: 2;
  }
}
</style>
